import { ICianMFUIElement } from '@cian/mf-registry/browser';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { registerNewbuildingBrokerModalsMicroFrontendManifest } from '../../../services/registerNewbuildingBrokerModalsMicroFrontendManifest';
import { INewbuildingBrokerModalsMicroFrontendApi } from '../../../types/microfrontends/newbuildingBrokerModalsMicroFrontendApi';
import { useApplicationContext } from '../../../utils/applicationContext';
import { TModalInvocationData, TOpenMicrofrontResultHandler } from '../types';

export function useMicrofrontAPI(resultCallback: TOpenMicrofrontResultHandler) {
  const { httpApi, logger, microfrontendsRegistry } = useApplicationContext();

  const microfrontRef = useRef<ICianMFUIElement>(null);
  const [microfrontManifest, setMicrofrontManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): INewbuildingBrokerModalsMicroFrontendApi => (microfrontRef.current?.instance?.customElement as any)?.api,
    [microfrontRef],
  );

  const callLocalAPI = useCallback(
    (api: INewbuildingBrokerModalsMicroFrontendApi, invocationData: TModalInvocationData): void => {
      const { type, params } = invocationData;

      if (type === 'selfRequestModal') {
        api['/v1/open-self-request-modal/'](params);
      } else if (type === 'bookingCallbackModal') {
        api['/v1/open-booking-callback-modal/'](params);
      }
    },
    [],
  );

  const invocationDataRef = useRef<TModalInvocationData | undefined>(undefined);
  const firstAPICallRef = useRef(true);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (microfrontManifest && firstAPICallRef.current) {
      firstAPICallRef.current = false;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          callLocalAPI(api, invocationDataRef.current as TModalInvocationData);
          resultCallback({ isError: false });
        } else {
          resultCallback({ isError: true });
        }
      }, 0);
    }
  }, [resultCallback, getLocalAPI, callLocalAPI, microfrontManifest]);

  const registerAndSetManifest = useCallback(
    async (invocationData: TModalInvocationData) => {
      const result = await registerNewbuildingBrokerModalsMicroFrontendManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими данными после установки в стейт и инициализации рефа.
        invocationDataRef.current = invocationData;

        setMicrofrontManifest(result);
      } else {
        resultCallback({ isError: true });
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openMicrofront = useCallback(
    async (invocationData: TModalInvocationData): Promise<void> => {
      if (microfrontManifest) {
        const api = getLocalAPI();
        callLocalAPI(api, invocationData);
        resultCallback({ isError: false });
      } else {
        await registerAndSetManifest(invocationData);
      }
    },
    [getLocalAPI, callLocalAPI, registerAndSetManifest, resultCallback, microfrontManifest],
  );

  return {
    microfrontManifest,
    microfrontRef,
    openMicrofront,
  };
}
